import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { FindAllBatchesRequest } from '@pccr-ifc/pccr-ifc';

import { FindBatchesQueryParams, GetReasonsRequest } from '../../models/batch';
import { getResourceListQueryKeyRoot } from '../../../shared/utils/resource-query-key';
import { convertGetReasonsResponse, convertSearchBatchesResponse } from '../converters/batch-converters';
import { useApprovalService } from '../services/use-approval-service';
import { displayErrors } from '../../../shared/utils/error';
import { DocumentsSearchResult, Reason } from '../../models/api/response';

export enum BatchQueryKey {
  FILTER = 'filter',
  REASONS = 'reasons',
}

export function useFindBatchesQuery({
  processed,
  page,
  pageSize,
  sortBy,
  sortDirection,
  search,
  groupId,
  filter,
  enabled,
}: FindBatchesQueryParams) {
  const documentService = useApprovalService();
  const { t } = useTranslation();

  return useQuery<DocumentsSearchResult, unknown>(
    [getResourceListQueryKeyRoot('BATCH'), page, pageSize, sortBy, sortDirection, search, groupId, filter, processed],
    async () => {
      const request: FindAllBatchesRequest = {
        processed,
        currentPage: page,
        limitOnPage: pageSize,
        sortBy,
        sortDirection,
        search,
        groupId,
        filter,
      };

      const response = await documentService.findBatches(request);
      return convertSearchBatchesResponse(response.data);
    },
    {
      enabled,
      onError: error => displayErrors(error, t),
      staleTime: 180000,
      // alle 180s neu laden, aber nur wenn die Abfrage aktiviert ist
      refetchInterval: enabled ? 180000 : false,
      refetchIntervalInBackground: true,
    }
  );
}

export function useGetFiltersQuery(processed: boolean, groupId: number, enabled: boolean) {
  const documentService = useApprovalService();
  const { t } = useTranslation();

  return useQuery(
    [BatchQueryKey.FILTER, groupId, processed],
    async () => {
      const response = await documentService.getFilters({ groupId, processed });
      return response.data;
    },
    {
      enabled,
      onError: error => displayErrors(error, t),
      refetchOnWindowFocus: false,
      cacheTime: 0,
      staleTime: 0,
    }
  );
}

export function useGetReasonsQuery(request: GetReasonsRequest, enabled: boolean) {
  const documentService = useApprovalService();
  const { t } = useTranslation();

  return useQuery<Reason[], unknown>(
    [BatchQueryKey.REASONS, request.lang],
    async () => {
      const response = await documentService.getRejectionReasons(request);
      return convertGetReasonsResponse(response.data);
    },
    {
      enabled,
      onError: error => displayErrors(error, t),
    }
  );
}
